/*
 * Copyright (c) 2026 CalAmp Corp. All Rights Reserved
 */
import React, {MouseEventHandler, useCallback} from 'react';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {EmissionTester} from '@ion/core/src/types/services';
import {isValidEmail} from '@ion/core/src/utils/Validators';
import {IconButton} from '@ion/dls/src/components/buttons/IconButton';
import FormItem from '@ion/dls/src/components/forms/FormItem';
import FormSection from '@ion/dls/src/components/forms/FormSection';
import FormTextField from '@ion/dls/src/components/forms/FormTextField';
import {CarbEditorFields} from '@ion/dls/src/editors/GroupEditorUtils';
import {FormState, validate} from '@ion/dls/src/hooks/useFormState';
import {ModifyIcon} from '@ion/dls/src/icons/ModifyIcon';
import Render from '@ion/dls/src/render/Render';
import {IconSize} from '@ion/dls/src/types/types';

export interface ComplianceFieldsProps {
  complianceValues: EmissionTester;
  isInfoForm?: boolean;
  onEdit?: MouseEventHandler;
  formState?: FormState;
  setFormState?: (value: FormState) => void;
};

const useStyles = makeStyles(() => {
  return {
    modifyIcon : {
      marginTop : 40,
      height    : 55,
      width     : 55
    },
    carbForm : {
      padding : '15px 0px'
    }
  };
});

/**
 * The ComplianceFields component renders a group of form fields containing CARB compliance specialist 
 * information (ID, Name and email).
 * 
 * ```typescript
 * import {ComplianceFields} from '@ion/applications/src/bulk/carb/ComplianceFields';
 * 
 * const SomeComponent = () => {
 *   return (
 *     <ComplianceFields  formState={formState}
 *      isInfoForm={true}
 *       ... />
 *   );
 * };
 * ```
 */
export const ComplianceFields = ({
  onEdit,
  formState,
  setFormState,
  complianceValues,
  isInfoForm = false
}: ComplianceFieldsProps) => {
  const classes = useStyles();

  const onFieldChange = useCallback(({target: {value, name}}) => {
    let error = false;
    if (name === CarbEditorFields.COMPLIANCE_SPECIALIST_EMAIL) {
      error = !validate(value, isValidEmail);
    }
    setFormState?.({
      [name] : {
        value    : value,
        error    : error,
        required : true
      }
    });
  }, [formState]);
    
  return (<>
    <FormSection>
      <FormItem disableMinWidth customWidth={isInfoForm ? 6 : 3} className={classes.carbForm}>
        <FormTextField required
          disabled={!isInfoForm}
          onChange={onFieldChange}
          fieldLabel={translate('admin-groups:carb.carbFields.complianceSpecialistId.label')}
          placeholder={translate('admin-groups:carb.carbFields.complianceSpecialistId.placeholder')}
          hasError={formState?.[CarbEditorFields.COMPLIANCE_SPECIALIST_ID]?.error}
          errorText={translate('admin-groups:carb.carbFields.complianceSpecialistId.error')}
          name={CarbEditorFields.COMPLIANCE_SPECIALIST_ID}
          value={formState?.[CarbEditorFields.COMPLIANCE_SPECIALIST_ID]?.value || complianceValues?.testerId} />
      </FormItem>
      <FormItem disableMinWidth customWidth={isInfoForm ? 6 : 4}>
        <FormTextField required
          disabled={!isInfoForm}
          onChange={onFieldChange}
          hasError={formState?.[CarbEditorFields.COMPLIANCE_SPECIALIST_NAME]?.error}
          errorText={translate('admin-groups:carb.carbFields.complianceSpecialistName.error')}
          fieldLabel={translate('admin-groups:carb.carbFields.complianceSpecialistName.label')}
          placeholder={translate('admin-groups:carb.carbFields.complianceSpecialistName.placeholder')}
          name={CarbEditorFields.COMPLIANCE_SPECIALIST_NAME}
          value={formState?.[CarbEditorFields.COMPLIANCE_SPECIALIST_NAME]?.value || complianceValues?.name} />
      </FormItem>
      <FormItem disableMinWidth customWidth={isInfoForm ? 6 : 4}>
        <FormTextField required
          disabled={!isInfoForm}
          onChange={onFieldChange}
          fieldLabel={translate('admin-groups:carb.carbFields.complianceSpecialistEmail.label')}
          placeholder={translate('admin-groups:carb.carbFields.complianceSpecialistEmail.placeholder')}
          hasError={formState?.[CarbEditorFields.COMPLIANCE_SPECIALIST_EMAIL]?.error}
          errorText={translate('admin-groups:carb.carbFields.complianceSpecialistEmail.error')}
          name={CarbEditorFields.COMPLIANCE_SPECIALIST_EMAIL}
          value={formState?.[CarbEditorFields.COMPLIANCE_SPECIALIST_EMAIL]?.value || complianceValues?.emailAddress} />
      </FormItem>
      <Render.If condition={!isInfoForm}>
        <IconButton icon={<ModifyIcon size={IconSize.medium}/>}
          className={classes.modifyIcon}
          onClick={onEdit} />
      </Render.If>
    </FormSection>
  </>);
};

export default ComplianceFields;
